import { Helmet } from "react-helmet-async";

interface JobPostingData {
  title: string;
  description: string;
  datePosted: string;
  validThrough?: string;
  employmentType: string;
  experienceRequirements: string;
  location: {
    name: string;
    addressCountry: string;
  };
  salary?: {
    min?: number;
    max?: number;
    currency: string;
  };
  hiringOrganization: {
    name: string;
    logo?: string;
    url?: string;
    address?: string;
  };
  jobUrl: string;
  skills?: string[];
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface OrganizationData {
  name: string;
  logo?: string;
  url?: string;
  description?: string;
  industry?: string;
  address?: string;
}

interface StructuredDataProps {
  type: 'JobPosting' | 'BreadcrumbList' | 'Organization' | 'WebSite';
  data: JobPostingData | BreadcrumbItem[] | OrganizationData | any;
}

export const StructuredData = ({ type, data }: StructuredDataProps) => {
  const generateJobPostingSchema = (jobData: JobPostingData) => ({
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": jobData.title,
    "description": jobData.description,
    "datePosted": jobData.datePosted,
    "validThrough": jobData.validThrough,
    "employmentType": jobData.employmentType,
    "experienceRequirements": jobData.experienceRequirements,
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": jobData.location.name,
        "addressCountry": jobData.location.addressCountry
      }
    },
    ...(jobData.salary && {
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": jobData.salary.currency,
        "value": {
          "@type": "QuantitativeValue",
          ...(jobData.salary.min && jobData.salary.max && {
            "minValue": jobData.salary.min,
            "maxValue": jobData.salary.max,
            "unitText": "YEAR"
          }),
          ...(jobData.salary.min && !jobData.salary.max && {
            "minValue": jobData.salary.min,
            "unitText": "YEAR"
          })
        }
      }
    }),
    "hiringOrganization": {
      "@type": "Organization",
      "name": jobData.hiringOrganization.name,
      ...(jobData.hiringOrganization.logo && { "logo": jobData.hiringOrganization.logo }),
      ...(jobData.hiringOrganization.url && { "url": jobData.hiringOrganization.url }),
      ...(jobData.hiringOrganization.address && {
        "address": {
          "@type": "PostalAddress",
          "addressLocality": jobData.hiringOrganization.address,
          "addressCountry": "LK"
        }
      })
    },
    "url": jobData.jobUrl,
    ...(jobData.skills && jobData.skills.length > 0 && {
      "skills": jobData.skills.join(", ")
    }),
    "industry": "Technology",
    "workHours": "Full-time",
    "salaryCurrency": jobData.salary?.currency || "LKR"
  });

  const generateBreadcrumbSchema = (breadcrumbs: BreadcrumbItem[]) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  });

  const generateOrganizationSchema = (orgData: OrganizationData) => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": orgData.name,
    ...(orgData.logo && { "logo": orgData.logo }),
    ...(orgData.url && { "url": orgData.url }),
    ...(orgData.description && { "description": orgData.description }),
    ...(orgData.industry && { "industry": orgData.industry }),
    ...(orgData.address && {
      "address": {
        "@type": "PostalAddress",
        "addressLocality": orgData.address,
        "addressCountry": "LK"
      }
    })
  });

  const generateWebSiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "GreatJobs.lk",
    "url": "https://greatjobs.lk",
    "description": "Find your dream job in Sri Lanka. Connect with top employers and discover thousands of opportunities.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://greatjobs.lk/jobs?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://www.linkedin.com/company/greatjobs-lk",
      "https://www.facebook.com/greatjobs.lk"
    ]
  });

  let schema;
  switch (type) {
    case 'JobPosting':
      schema = generateJobPostingSchema(data as JobPostingData);
      break;
    case 'BreadcrumbList':
      schema = generateBreadcrumbSchema(data as BreadcrumbItem[]);
      break;
    case 'Organization':
      schema = generateOrganizationSchema(data as OrganizationData);
      break;
    case 'WebSite':
      schema = generateWebSiteSchema();
      break;
    default:
      return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};